'use client';
import { useResource } from '@/lib/use-resource';

interface Row {
  sector: string;
  portfolioWeight: number;
  benchmarkWeight: number;
  deltaPp: number;
}

const SCALE = 16; // pp per visual unit; tune for biggest deltas to fit

export function SectorVsBenchmark({ dataUrl = '/api/portfolio-snapshot', title = 'Sector vs SPY' }: { dataUrl?: string; title?: string } = {}) {
  const { data: snap } = useResource<{ sectorVsBenchmark?: Row[] }>(dataUrl);
  const rows = snap?.sectorVsBenchmark ?? null;

  if (!rows) return null;

  return (
    <section>
      <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
        <h2 className="text-[13px] font-semibold text-white tracking-wide uppercase">{title}</h2>
        <div className="text-[10px] text-[#64748b]">over/underweight in pp · sorted by absolute size</div>
      </div>
      <div className="bg-[#0d1117] border border-[#1e293b] rounded-lg p-4 space-y-2">
        {rows.map((r) => {
          const overweight = r.deltaPp > 0;
          const widthPct = Math.min(50, Math.abs(r.deltaPp) * SCALE / 16);
          return (
            <div key={r.sector} className="grid grid-cols-12 gap-2 items-center text-[12px]">
              <div className="col-span-3 text-[#cbd5e1] truncate">{r.sector}</div>
              <div className="col-span-1 text-right tabular-nums text-[10px] text-[#64748b]">
                {(r.portfolioWeight * 100).toFixed(0)}%
              </div>
              <div className="col-span-7 flex items-center">
                <div className="relative w-full h-3 bg-[#0a0e14] rounded">
                  <div className="absolute left-1/2 top-0 bottom-0 w-px bg-[#475569]" />
                  {!overweight && r.deltaPp !== 0 && (
                    <div
                      className="absolute right-1/2 top-0 bottom-0 bg-red-500/40 rounded-l"
                      style={{ width: `${widthPct}%` }}
                    />
                  )}
                  {overweight && (
                    <div
                      className="absolute left-1/2 top-0 bottom-0 bg-emerald-500/50 rounded-r"
                      style={{ width: `${widthPct}%` }}
                    />
                  )}
                </div>
              </div>
              <div className={`col-span-1 text-right tabular-nums font-semibold ${overweight ? 'text-emerald-400' : r.deltaPp < 0 ? 'text-red-400' : 'text-[#94a3b8]'}`}>
                {r.deltaPp >= 0 ? '+' : ''}{r.deltaPp.toFixed(1)}
              </div>
            </div>
          );
        })}
        <div className="text-[10px] text-[#64748b] pt-2 border-t border-[#1e293b]">
          Benchmark: S&amp;P 500 sector mix (Q1 2026 GICS-grouped). Index ETFs in your book are excluded — this view is your active picks vs SPY.
        </div>
      </div>
    </section>
  );
}
